import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

function loadRecaptchaScript({ useRecaptchaNet = false } = {}) {
  if (window.grecaptcha) return Promise.resolve(window.grecaptcha);
  if (window.__recaptchaScriptLoading) return window.__recaptchaScriptLoading;

  const srcBase = useRecaptchaNet
    ? "https://www.recaptcha.net/recaptcha/api.js"
    : "https://www.google.com/recaptcha/api.js";

  window.__recaptchaScriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `${srcBase}?render=explicit`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.grecaptcha);
    s.onerror = reject;
    document.body.appendChild(s);
  });

  return window.__recaptchaScriptLoading;
}

const ReCaptchaV2 = forwardRef(
  (
    {
      siteKey,
      theme = "light",        // "light" | "dark"
      size = "normal",        // "normal" | "compact"
      tabindex = 0,
      useRecaptchaNet = false, // set true if google.com blocked locally
      onVerify = () => {},
      onExpired = () => {},
      className = "",
      style,
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useImperativeHandle(ref, () => ({
      getResponse: () =>
        window.grecaptcha && widgetIdRef.current != null
          ? window.grecaptcha.getResponse(widgetIdRef.current)
          : "",
      reset: () => {
        if (window.grecaptcha && widgetIdRef.current != null) {
          window.grecaptcha.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      let mounted = true;

      const renderWidget = () => {
        if (!mounted) return;
        if (!window.grecaptcha || !containerRef.current) return;

        // prevent double render
        if (containerRef.current.hasChildNodes()) return;

        const id = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          tabindex,
          callback: (token) => onVerify(token),
          "expired-callback": () => onExpired(),
        });
        widgetIdRef.current = id;
      };

      loadRecaptchaScript({ useRecaptchaNet })
        .then(() => {
          // grecaptcha may still be initializing; retry until ready
          const tryRender = () => {
            if (window.grecaptcha && window.grecaptcha.render) {
              renderWidget();
              return true;
            }
            return false;
          };
          if (!tryRender()) {
            const interval = setInterval(() => {
              if (tryRender()) clearInterval(interval);
            }, 250);
          }
        })
        .catch(() => {
          // Optional: surface a UI message if the script fails to load
          // (ad blockers, offline, etc.)
        });

      return () => {
        mounted = false;
      };
    }, [siteKey, theme, size, tabindex, useRecaptchaNet]);

    return <div ref={containerRef} className={className} style={style} />;
  }
);

export default ReCaptchaV2;
